<template>
  <Modal :size="visit.rsvId ? '' : 'big'" :title="title" :loading="visits.busy || edit.busy" @close="onClose">
    <template v-if="statusLabel" #header-right>
      <span class="status" :class="{ bad }">{{ statusLabel }}</span>
    </template>
    <template #content>
      <div class="contents">
        <div v-if="visit.rsvId" class="detail">
          <h1>{{ visit.store }}</h1>
          <ul>
            <li>
              <div class="item"><ClockIcon />{{ visit.time }}</div>
            </li>
            <li>
              <div class="item"><AddPeopleIcon />{{ visit.persons }}名</div>
            </li>
            <li>
              <div class="item"><TableIcon />{{ visit.table.length ? visit.table.join('、') : '未指定' }}</div>
            </li>
            <li v-if="visit.course">
              <div v-for="(course, i) in visit.course" :key="i" class="item"><KnifeForkIcon />{{ course }}</div>
            </li>
            <li v-if="visit.medium">予約媒体：{{ visit.medium }}</li>
            <li v-if="visit.settlement">決済情報：{{ visit.settlement }}</li>
            <li v-if="visit.remarks" class="remarks">備考：{{ visit.remarks }}</li>
          </ul>
          <div v-if="visit.ordersExistence" class="order-existence">
            <h2>喫食情報</h2>
            <ul class="account-info">
              <li>
                <span><ClockIcon />来店時間</span><span>{{ orders.detail.visitTime }}</span>
              </li>
              <li>
                <span><ClockIcon />退店時間</span><span>{{ orders.detail.payTime }}</span>
              </li>
              <li class="persons">
                <span><AddPeopleIcon />人数</span><span>{{ orders.detail.persons }}</span>
              </li>
              <li class="payment">
                <span>お支払金額</span><span>¥{{ orders.detail.payment }}</span>
              </li>
            </ul>
            <hr />
            <ul class="order">
              <li v-for="(item, index) in orders.detail.menu" :key="index">
                <span>{{ item.name }}</span>
                <span class="price">¥{{ item.price }}</span>
                <span class="qty">{{ item.qty }}</span>
              </li>
            </ul>
          </div>
        </div>
        <template v-else>
          <FormGrid class="form">
            <template v-if="isEdit">
              <dt class="required">来店日時</dt>
              <dd>
                <CalendarInput
                  v-model="reserveDate"
                  placeholder="−−−−年−−月−−日(ー)"
                  :format="displayDate"
                  :holidayList="allHolidays"
                  :reserveCalendar="reserveCalendar"
                  :busy="busy"
                  :isSP="isSP"
                  @setDate="setDate"
                  @beforeShow="setDate(today())"
                />
                <div class="time">
                  <TimeInput v-model="startTime" :startHour="6" clearButton />
                  〜
                  <TimeInput v-model="endTime" :startHour="6" clearButton />
                </div>
              </dd>
            </template>
            <template v-for="{ id, label, type } in currentStore.optionsSchema.visitHistory">
              <template v-if="isEdit || visit.options[id]">
                <dt :key="`title-${id}`">{{ label }}</dt>
                <dd :key="`value-${id}`">
                  <template v-if="isEdit">
                    <CalendarInput
                      v-if="type === 2"
                      v-model="visit.options[id]"
                      :holidayList="allHolidays"
                      :reserveCalendar="reserveCalendar"
                      :busy="busy"
                      :isSP="isSP"
                      @setDate="setDate"
                      @beforeShow="setDate(today())"
                    />
                    <component
                      v-else
                      :is="componentByType(type)"
                      :value="visit.options[id]"
                      @input="update({ options: { [id]: $event } })"
                      @change="update({ options: { [id]: $event } })"
                    />
                  </template>
                  <template v-else>{{ visit.options[id] }}</template>
                </dd>
              </template>
            </template>
          </FormGrid>
        </template>
      </div>
    </template>
    <template v-if="isEdit" #footer-center>
      <Button small @click="onCancel">キャンセル</Button>
      <Button small primary :disabled="!visit.reserveDate || !changed" @click="save">保存</Button>
    </template>
    <template v-else-if="visit.id" #footer-right>
      <Button small @click="toEdit" :disabled="GNAVI_MAINTENANCE"><EditIcon slot="icon" />編集</Button>
      <Button small @click="remove" :disabled="GNAVI_MAINTENANCE"><DeleteIcon slot="icon" />削除</Button>
    </template>
  </Modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { formatDate, splitDate, toHourClock, today } from '@/utils/date'
import { models } from '@/utils/vue'
import TimeInput from '@/components/molecules/TimeInput'
import FormGrid from '@/components/molecules/FormGrid'
import Modal from '@/components/molecules/Modal'
import Dialog from '@/components/molecules/ModalDialog'
import CalendarInput from '@/components/molecules/CalendarInput'
import Button from '@/components/atoms/Button'
import TextInput from '@/components/atoms/TextInput'
import TextArea from '@/components/atoms/TextArea'
import AddPeopleIcon from '@/components/icons/addPeople'
import ClockIcon from '@/components/icons/clock'
import DeleteIcon from '@/components/icons/delete'
import EditIcon from '@/components/icons/edit'
import KnifeForkIcon from '@/components/icons/knifeFork'
import TableIcon from '@/components/icons/table'

export default {
  name: 'VisitHistoryModal',
  components: {
    Button,
    TextInput,
    TextArea,
    TimeInput,
    FormGrid,
    Modal,
    Dialog,
    CalendarInput,
    AddPeopleIcon,
    ClockIcon,
    DeleteIcon,
    EditIcon,
    KnifeForkIcon,
    TableIcon,
  },
  props: {
    index: Number,
  },
  data() {
    return {
      isEdit: this.index === null,
      GNAVI_MAINTENANCE,
    }
  },
  computed: {
    ...mapState({
      currentStore: state => state.stores.current.store,
      visits: state => state.customers.detail.visits,
      edit: state => state.customers.detail.visits.edit,
      orders: state => state.orders,
      busy: state => state.init.busy,
      reserveCalendar: state => state.init.reserveCalendar,
      isSP: state => state.app.isSP,
    }),
    ...mapGetters({
      allHolidays: 'holidays/all',
      changed: 'customers/detail/visits/edit/changed',
    }),
    ...models(
      ['reserveDate', 'startTime', 'endTime'],
      ({ visit }, key) => visit[key],
      ({ update }, key, value) => update({ [key]: value }),
    ),
    visit() {
      return this.isEdit ? this.edit.visit : this.visits.list[this.index]
    },
    title() {
      if (this.isEdit) return `来店履歴の${this.visit.id ? '編集' : '追加'}`
      const { year, month, day } = splitDate(this.visit.date)
      const date = year ? formatDate(this.visit.date, 'YYYY/M/D (dd)') : `${month}/${day}`
      return `${date} ${this.visit.time}`
    },
    statusLabel() {
      const { visitStatus: status, date, time } = this.visit
      if (status === 0) return '（無断キャンセル）'
      if (status === 1) return '（事前キャンセル）'
      if (status === 3 || status === 4 || status === 10) {
        const now = toHourClock(new Date())
        if (now.date < date || (now.date === date && now.time < time.split('〜')[0])) {
          return '（来店前）'
        }
      }
      return ''
    },
    bad() {
      return this.visit.visitStatus === 0
    },
  },
  mounted() {
    if (this.index === null) {
      this.toEdit()
    } else if (this.visit.ordersExistence) {
      this.$store.dispatch('orders/get', this.visit.rsvId)
    }
  },
  methods: {
    today,
    displayDate(date) {
      const { year, month, day } = splitDate(date)
      return year ? formatDate(date, 'YYYY年M月D日 (dd)') : `${month}月${day}日`
    },
    toEdit() {
      this.$store.dispatch('customers/detail/visits/edit/start', this.index !== null ? this.visit.id : null)
      this.isEdit = true
    },
    componentByType(type) {
      return type === 6 ? TextArea : TextInput
    },
    update(visit) {
      this.$store.commit('customers/detail/visits/edit/update', visit)
    },
    async save() {
      if (await this.$store.dispatch('customers/detail/visits/edit/save')) {
        this.$emit('change')
        this.$emit('close')
      }
    },
    async remove() {
      if (
        (await Dialog.confirm({
          title: '来店履歴削除',
          message: '以下の来店履歴を削除してもよろしいですか？',
          target: this.title,
          caution: true,
          cancelText: '戻る',
          okText: '削除',
        })) &&
        (await this.$store.dispatch('customers/detail/visits/remove', this.visit.id))
      ) {
        this.$emit('change')
        this.$emit('close')
      }
    },
    async onCancel() {
      if (!(await this.confirmDisposeDialog())) return
      if (this.visit.id) {
        this.isEdit = false
      } else {
        this.$emit('close')
      }
    },
    async onClose() {
      if (!(await this.confirmDisposeDialog())) return
      this.$emit('close')
    },
    async confirmDisposeDialog() {
      if (!this.isEdit || !this.changed) return true
      const mode = this.visit.id ? '編集' : '入力'
      return Dialog.confirm({
        title: `来店履歴の${mode}`,
        message: `${mode}中のデータを破棄してよろしいですか？`,
        caution: true,
        cancelText: '戻る',
        okText: '破棄',
      })
    },
    async setDate(date) {
      await this.$store.dispatch('init/createCalendar', { date })
    },
  },
}
</script>

<style lang="postcss" scoped>
.contents {
  min-width: 343px;
  padding: 24px 32px;

  .responsive & {
    @media screen and (--sp-modal) {
      padding: 28px 16px;
    }
  }
}

.detail {
  display: grid;
  grid-gap: 16px;
  grid-template-rows: max-content 1fr;
  text-align: left;

  h1,
  h2 {
    font-size: 15px;
    height: 32px;
  }

  > ul {
    display: grid;
    grid-gap: 8px;
    margin: 0 0 8px 16px;
  }

  li {
    align-items: center;
    display: grid;
  }

  .item {
    display: flex;
  }

  svg {
    fill: var(--very-dark-gray-2);
    height: 16px;
    margin-right: 8px;
    width: 16px;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      > h1,
      h2 {
        height: auto;
      }
    }
  }
}

.order-existence {
  display: grid;
  grid-gap: 16px 0;

  hr {
    background-color: var(--light-gray);
    border: none;
    height: 1px;
    margin: 4px 0;
    width: 100%;
  }

  .account-info,
  .order {
    display: grid;
    padding: 0 16px;

    li {
      display: grid;
      grid-auto-flow: column;
      grid-gap: 0 16px;

      svg {
        vertical-align: text-bottom;
      }
    }
  }

  .account-info {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 8px 40px;
    grid-template-rows: 24px 24px;

    li.persons,
    li.payment {
      grid-gap: 0 24px;
      grid-template-columns: 80px 1fr;
    }
  }

  .order {
    grid-gap: 8px 0;

    li {
      grid-template-columns: 208px 1fr 1fr;
    }

    .price,
    .qty {
      text-align: right;
    }
  }

  .responsive & {
    @media screen and (--sp-modal) {
      .account-info {
        grid-auto-flow: row;

        li {
          display: flex;
          justify-content: space-between;
        }
      }

      .order {
        grid-gap: 16px 0;

        li {
          grid-template-columns: 1fr max-content 40px;
        }
      }
    }
  }
}

.form {
  grid-template-columns: 88px 1fr;

  > dt {
    font-size: 13px;
    font-weight: normal;
  }

  > dd {
    font-size: 18px;
  }
}

.time {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  grid-template-columns: 1fr 18px 1fr;
  justify-content: flex-start;
}

.required::after {
  align-items: center;
  background: #e53935;
  color: var(--white);
  content: '必須';
  display: inline-flex;
  font-size: 12px;
  height: 16px;
  justify-content: center;
  margin-left: 3px;
  white-space: nowrap;
  width: 33px;
}

>>> .calendar-button {
  width: 100%;
}

.status {
  font-size: 13px;
  font-weight: bold;

  &.bad {
    color: var(--pure-red);
  }
}

.remarks {
  white-space: pre-line;
}

.v-popover {
  width: auto;
}
</style>
